import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { dt } from "@/lib/format";
import { PageHeader, Pill, Table, Td, Th } from "@/components/ui";
import { PaginationBottom, PaginationTop } from "@/components/Pagination";

interface BroadcastItem {
  id: number;
  message_text: string;
  sent_count: number;
  failed_count: number;
  created_at: string;
  admin_id: number;
}

interface HistoryResponse {
  items: BroadcastItem[];
  total: number;
}

export default function Broadcasts() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const offset = (page - 1) * pageSize;

  const q = useQuery<HistoryResponse>({
    queryKey: ["broadcasts", "history", page, pageSize],
    queryFn: () =>
      api(`/api/admin/broadcasts?limit=${pageSize}&offset=${offset}`),
  });

  const items = q.data?.items ?? [];
  const total = q.data?.total ?? 0;

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="История рассылок"
        right={<div className="text-zinc-400 text-sm">всего: {total}</div>}
      />

      {total > 0 && (
        <div className="flex justify-end mb-3">
          <PaginationTop
            total={total}
            pageSize={pageSize}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        </div>
      )}

      {q.isLoading && <div className="text-zinc-400">Загрузка…</div>}

      {!q.isLoading && items.length === 0 && (
        <div className="text-zinc-500">Ещё ничего не рассылали.</div>
      )}

      {items.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th>id</Th>
              <Th>дата</Th>
              <Th>текст</Th>
              <Th>отправлено</Th>
              <Th>ошибок</Th>
              <Th>admin</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id}>
                <Td className="text-zinc-500">{b.id}</Td>
                <Td className="text-zinc-300 whitespace-nowrap">{dt(b.created_at)}</Td>
                <Td className="text-zinc-300">
                  <div className="max-w-[60ch] truncate" title={b.message_text}>
                    {b.message_text}
                  </div>
                </Td>
                <Td>
                  <Pill tone="success">{b.sent_count}</Pill>
                </Td>
                <Td>
                  {b.failed_count > 0 ? (
                    <Pill tone="warning">{b.failed_count}</Pill>
                  ) : (
                    <span className="text-zinc-600">0</span>
                  )}
                </Td>
                <Td className="text-zinc-500 font-mono">#{b.admin_id}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {items.length > 0 && total > pageSize && (
        <PaginationBottom
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
